import { useState, useCallback, useMemo } from 'react';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Table } from '@tigergraph/app-ui-lib/table';
import { ValidateResult } from '@tigergraph/tools-models/utils';
import { EmbeddingAttributeSourceSelect } from '@/components/schemaDesigner/attributeEditorContainer/attributeEditor/EmbeddingAttributeSourceSelect';
import { Checkbox } from '@tigergraph/app-ui-lib/checkbox';
import { buildEmbeddingAttributeEditorRow } from '@/components/schemaDesigner/attributeEditorContainer/attributeEditor/buildEmbeddingAttributeEditorRow';
import TooltipLabel from '@/components/TooltipLabel';
import { Button } from '@tigergraph/app-ui-lib/button';
import { randomString } from '@/utils/schemaDesigner';
import { expand } from 'inline-style-expand-shorthand';
import { MdDeleteOutline } from 'react-icons/md';
import { Plus } from 'baseui/icon';
import ErrorMessage from '@/components/ErrorMessage';
import ConfirmModal from '@/components/ConfirmModal';
import { mergeOverrides } from 'baseui';
import { SchemaAttributeTableWithSelect } from '@/utils/schemaDesigner/styleObject';
import { Overrides } from 'baseui/overrides';
import { StatefulPopover } from '@tigergraph/app-ui-lib/popover';
import { TRIGGER_TYPE } from 'baseui/popover';
import { EmbeddingAttribute } from '@tigergraph/tools-models/topology/embedding-attribute.model';
import { useWorkspaceContext } from '@/contexts/workspaceContext';
import { compare } from 'compare-versions';
import {
  LoadingJobData,
  LoadToVectorData,
  OneColumnMappingDataSourceColumnSource,
  OneColumnMappingGraphEntityTarget,
  OneColumnMappingMappingWidgetSource,
  OneColumnMappingMappingWidgetTarget,
  OneColumnMappingTargetType,
} from '@tigergraph/tools-models';

export interface EmbeddingAttributeEditorProps {
  attributes: EmbeddingAttribute[];
  validateResult: ValidateResult | undefined;
  mappingValidateResult?: ValidateResult | undefined;
  loadingJob?: LoadingJobData;
  loadingStatements?: LoadToVectorData[];
  readOnly?: boolean;
  maxHeight?: string;
  onSave: (attributes: EmbeddingAttribute[]) => void;
  onDeleteLoadingStatements?: (vectorNames: string[]) => void;
  onChangeLoadingStatementMapping?: (
    vectorName: string,
    loadingStatement: LoadToVectorData,
    mappings?: {
      mappingSource: OneColumnMappingDataSourceColumnSource | OneColumnMappingMappingWidgetSource;
      mappingTarget: OneColumnMappingGraphEntityTarget | OneColumnMappingMappingWidgetTarget;
    }[]
  ) => void;
}

export default function EmbeddingAttributeEditor(props: EmbeddingAttributeEditorProps) {
  const [css, theme] = useStyletron();
  const {
    attributes,
    loadingJob,
    loadingStatements,
    readOnly,
    maxHeight,
    validateResult,
    mappingValidateResult,
    onSave,
    onDeleteLoadingStatements,
    onChangeLoadingStatementMapping,
  } = props;
  const { currentWorkspace } = useWorkspaceContext();

  const [openDialog, setOpenDialog] = useState(false);
  const [deleteSelection, setDeleteSelection] = useState<{ [index: number]: boolean }>({});

  const supportEditingEmbeddingAttributes = useMemo(() => {
    return currentWorkspace?.tg_version && compare(currentWorkspace?.tg_version, '4.1.x', '>');
  }, [currentWorkspace?.tg_version]);

  const supportMappingEmbeddingAttributes = useMemo(() => {
    return currentWorkspace?.tg_version && compare(currentWorkspace?.tg_version, '4.2.0', '>');
  }, [currentWorkspace?.tg_version]);

  const handleAddAttribute = () => {
    const attr = new EmbeddingAttribute();
    attr.name = `newAttr_${randomString(3)}`;
    if (attributes.length > 0) {
      attr.embeddingAttrId = attributes[attributes.length - 1].embeddingAttrId + 1;
    }
    attr.dimension = 128;
    attr.metric = 'COSINE';
    attr.indexType = 'HNSW';
    attr.type = 'FLOAT';
    const newAttributes = [...attributes, attr];
    onSave(newAttributes);
  };

  const handleDeleteAttributesConfirm = () => {
    setOpenDialog(true);
  };

  const handleDeleteAttributes = () => {
    const newAttributes = [];
    for (let i = 0; i < attributes.length; i++) {
      if (!(i in deleteSelection)) {
        newAttributes.push(attributes[i]);
      }
    }
    setDeleteSelection({});
    onSave(newAttributes);
    onDeleteLoadingStatements?.(Object.keys(deleteSelection).map((index) => attributes[Number(index)].name));
  };

  const handleDeleteSelection = useCallback((index: number) => {
    setDeleteSelection((prevDeleteSelection) => {
      const isCheck = !prevDeleteSelection[index];
      if (isCheck) {
        return {
          ...prevDeleteSelection,
          [index]: !prevDeleteSelection[index],
        };
      } else {
        delete prevDeleteSelection[index];
        return { ...prevDeleteSelection };
      }
    });
  }, []);

  const handleHeaderDeleteSelection = useCallback(() => {
    setDeleteSelection((prevDeleteSelection) => {
      if (Object.keys(deleteSelection).length === attributes.length) {
        return {};
      } else {
        for (let i = 0; i < attributes.length; i++) {
          prevDeleteSelection[i] = true;
        }
        return { ...prevDeleteSelection };
      }
    });
  }, [attributes.length, deleteSelection]);

  const isInvalidEmbeddingAttribute = useMemo(() => {
    return validateResult && !validateResult.success && validateResult.message?.includes('vector attribute');
  }, [validateResult]);

  const tableHeader = useMemo(() => {
    const tableHeader = [
      <Checkbox
        key={'delete_checkbox'}
        isIndeterminate={
          Object.keys(deleteSelection).length > 0 && Object.keys(deleteSelection).length !== attributes.length
        }
        checked={Object.keys(deleteSelection).length > 0 && Object.keys(deleteSelection).length === attributes.length}
        disabled={readOnly || !supportEditingEmbeddingAttributes}
        onChange={handleHeaderDeleteSelection}
      />,
      'Name',
      'Dimension',
      'Metric',
      'Index Type',
      'Data Type',
    ];
    if (loadingJob && loadingStatements && supportMappingEmbeddingAttributes) {
      tableHeader.push('Source');
    }
    return tableHeader;
  }, [
    attributes.length,
    deleteSelection,
    handleHeaderDeleteSelection,
    loadingJob,
    loadingStatements,
    readOnly,
    supportEditingEmbeddingAttributes,
    supportMappingEmbeddingAttributes,
  ]);

  const tableData = useMemo(
    () =>
      attributes.map((attribute, index) => {
        const loadingStatement = loadingStatements?.find((statement) => statement.vectorName === attribute.name);
        const row = buildEmbeddingAttributeEditorRow({
          css,
          theme,
          attribute,
          deleting: !!deleteSelection[index],
          readOnly: readOnly,
          handleAttributeChange: (newAttribute) => {
            const newAttributes = [...attributes];
            newAttributes[index] = newAttribute;
            onSave(newAttributes);
            if (loadingStatement) {
              onChangeLoadingStatementMapping?.(attribute.name, {
                ...loadingStatement,
                vectorName: newAttribute.name,
              });
            }
          },
          handleDeleteSelection: () => handleDeleteSelection(index),
        });
        if (
          loadingJob &&
          loadingStatements?.length === attributes.length &&
          loadingStatement &&
          supportMappingEmbeddingAttributes
        ) {
          row.push(
            <div
              key={`Source_${attribute.name}_${index}`}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
            >
              <EmbeddingAttributeSourceSelect
                attribute={attribute}
                loadingJob={loadingJob}
                loadingStatement={loadingStatement}
                columnMapping={loadingStatement.mappings[1]}
                disabled={!supportMappingEmbeddingAttributes}
                onChangeSources={(mappingSource) =>
                  onChangeLoadingStatementMapping?.(attribute.name, loadingStatement, [
                    {
                      mappingSource,
                      mappingTarget: {
                        // @ts-ignore
                        type: OneColumnMappingTargetType.GraphEntity,
                        mappingIndex: 1,
                      },
                    },
                  ])
                }
              />
            </div>
          );
        }

        return row;
      }),
    [
      attributes,
      css,
      deleteSelection,
      handleDeleteSelection,
      loadingJob,
      loadingStatements,
      onChangeLoadingStatementMapping,
      onSave,
      readOnly,
      supportMappingEmbeddingAttributes,
      theme,
    ]
  );

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        rowGap: '8px',
      })}
    >
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        })}
      >
        <TooltipLabel
          label="Vector Attributes"
          tooltip={
            supportEditingEmbeddingAttributes
              ? 'You can update the vector attributes.'
              : "Don't support editing vector attributes in the current version."
          }
        />
        <StatefulPopover
          triggerType={TRIGGER_TYPE.hover}
          content={Object.keys(deleteSelection).length > 0 ? 'Delete vector attribute' : 'Add vector attribute'}
          overrides={{
            Body: {
              style: {
                ...expand({
                  margin: '10px',
                }),
              },
            },
          }}
        >
          <Button
            aria-label={Object.keys(deleteSelection).length > 0 ? 'Delete vector attribute' : 'Add vector attribute'}
            onClick={Object.keys(deleteSelection).length > 0 ? handleDeleteAttributesConfirm : handleAddAttribute}
            disabled={readOnly || !supportEditingEmbeddingAttributes}
            size="default"
            kind={Object.keys(deleteSelection).length ? 'destructive' : 'secondary'}
            overrides={{
              BaseButton: {
                style: {
                  ...expand({
                    padding: '3px',
                  }),
                },
              },
            }}
          >
            {Object.keys(deleteSelection).length > 0 ? <MdDeleteOutline size={16} /> : <Plus size={16} />}
          </Button>
        </StatefulPopover>
      </div>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          rowGap: '8px',
        })}
      >
        <Table
          divider={'horizontal'}
          overrides={mergeOverrides(SchemaAttributeTableWithSelect(theme) as Overrides<any>, {
            Root: {
              style: {
                maxHeight: maxHeight ?? '100%',
                overflow: 'auto',
              },
            },
            TableBodyRow: {
              style: ({ $rowIndex }) => {
                const match = validateResult?.message?.match(/"([^"]*)"/);
                if (match && match[1] !== undefined) {
                  const quotedWord = match[1];
                  if ($rowIndex === attributes.findIndex((attribute) => attribute.name === quotedWord)) {
                    return {
                      border: `2px solid ${theme.colors.negative}`,
                    };
                  }
                }
                return {};
              },
            },
          })}
          columns={tableHeader}
          data={tableData}
        />
        {isInvalidEmbeddingAttribute && <ErrorMessage message={validateResult?.message ?? ''} />}
        {mappingValidateResult && !mappingValidateResult.success && (
          <ErrorMessage message={mappingValidateResult.message ?? ''} />
        )}
      </div>
      <ConfirmModal
        open={openDialog}
        header="Warning"
        body={`Are you sure you want to delete the selected vector attribute${
          Object.keys(deleteSelection).length > 1 ? 's' : ''
        }?`}
        onConfirm={() => {
          setOpenDialog(false);
          handleDeleteAttributes();
        }}
        onCancel={() => setOpenDialog(false)}
      />
    </div>
  );
}
